import { Component, OnInit, ViewChild, Input } from '@angular/core';
import { Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { StandortService, Standort } from '../services/standort.service';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-standorte',
  templateUrl: './standorte.component.html',
  styleUrls: ['./standorte.component.scss']
})
export class StandorteComponent implements OnInit {
  @Input() kundeId: string;
  @Input() isEmbedded: boolean = false;
  @Input() parentContext?: string;
  
  displayedColumns: string[] = ['name', 'nummer', 'status', 'action'];
  dataSource: MatTableDataSource<Standort>;
  
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  loading = false;
  totalItems = 0;
  currentPage = 1;
  pageSize = 10;
  searchTerm = '';

  constructor(
    private standortService: StandortService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router
  ) {
    this.dataSource = new MatTableDataSource<Standort>([]);
  }

  ngOnInit(): void {
    console.log('✅ StandorteComponent ngOnInit - Component loaded successfully');
    console.log('📍 Current URL:', window.location.href);
    console.log('🎯 Component should be loaded within shell');
    console.log('🔍 Component Context:', {
      isEmbedded: this.isEmbedded,
      parentContext: this.parentContext
    });
    
    if (this.isEmbedded) {
      this.showEmbeddedUI();
    } else {
      this.showStandaloneUI();
    }
    
    this.loadStandorte();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  loadStandorte(page: number = 1): void {
    this.loading = true;
    this.currentPage = page;

    this.standortService.getStandorte(
      page, 
      this.pageSize, 
      this.searchTerm, 
      this.sort?.active || 'name', 
      this.sort?.direction || 'asc',
      this.kundeId
    ).subscribe({
      next: (response) => {
        this.dataSource.data = response.data;
        this.totalItems = response.total || 0;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading standorte:', error);
        this.snackBar.open('Fehler beim Laden der Standorte', 'Schließen', { duration: 3000 });
        this.loading = false;
      }
    });
  }

  onSearch(): void {
    this.loadStandorte(1);
  }

  onPageChange(event: any): void {
    this.pageSize = event.pageSize;
    this.loadStandorte(event.pageIndex + 1);
  }

  onSortChange(event: any): void {
    this.loadStandorte(1);
  }

  addStandort(): void {
    if (this.isEmbedded && this.parentContext === 'client-edit') {
      // In embedded mode within client-edit, navigate to standort-edit with return context
      this.router.navigate(['/standort-edit/new'], { 
        queryParams: { 
          returnTo: 'client-edit',
          kundeId: this.kundeId 
        } 
      });
    } else {
      // Standalone mode - normal navigation
      this.router.navigate(['/standort-edit/new']);
    }
  }



  editStandort(standort: Standort): void {
    if (this.isEmbedded && this.parentContext === 'client-edit') {
      // In embedded mode within client-edit, navigate to standort-edit with return context
      this.router.navigate(['/standort-edit', standort.id.toString()], { 
        queryParams: { 
          returnTo: 'client-edit',
          kundeId: this.kundeId 
        } 
      });
    } else {
      // Standalone mode - normal navigation
      this.showPageDetail(standort.id.toString());
    }
  }

  showEmbeddedUI(): void {
    console.log('🎯 Showing embedded UI for standorte component');
    // Hide header elements, adjust styling for embedded view
    // This will be handled in the template with conditional rendering
  }

  showStandaloneUI(): void {
    console.log('🎯 Showing standalone UI for standorte component');
    // Show full header, navigation, etc. for standalone view
    // This will be handled in the template with conditional rendering
  }

  showPageDetail(standortId: string): void {
    console.log('showPageDetail called with standortId:', standortId, 'type:', typeof standortId);

    // Validate standortId
    if (!standortId || standortId === '0' || standortId === 'new') {
      console.log('Invalid standortId, navigating to new location form');
      if (this.isEmbedded && this.parentContext === 'client-edit') {
        this.router.navigate(['/standort-edit/new'], { 
          queryParams: { 
            returnTo: 'client-edit',
            kundeId: this.kundeId 
          } 
        });
      } else {
        this.router.navigate(['/standort-edit/new']);
      }
    } else {
      console.log('Navigating to edit location with ID:', standortId);
      if (this.isEmbedded && this.parentContext === 'client-edit') {
        this.router.navigate(['/standort-edit', standortId], { 
          queryParams: { 
            returnTo: 'client-edit',
            kundeId: this.kundeId 
          } 
        });
      } else {
        this.router.navigate(['/standort-edit', standortId]);
      }
    }
  }

  getStatusText(status: any): string {
    if (!status) {
      return 'Kein Status';
    }
    
    // Handle case where status is an object (e.g., {id: 1, beschreibung: 'Aktiv'})
    if (typeof status === 'object' && status.id) {
      const statusFromEnv = (environment as any).standorte_status?.find((s: any) => s.id === status.id);
      return statusFromEnv ? statusFromEnv.label : (status.beschreibung || `Status ${status.id}`);
    }
    
    // Handle case where status is a number
    if (typeof status === 'number') {
      const statusFromEnv = (environment as any).standorte_status?.find((s: any) => s.id === status);
      return statusFromEnv ? statusFromEnv.label : `Status ${status}`;
    }
    
    return 'Unbekannt';
  }

  /**
   * Get CSS classes for status badge based on status object
   */
  getStatusClasses(status: any): string {
    if (status && status.bg_color && status.text_color && status.ring_color && 
        status.dark_bg_color && status.dark_text_color && status.dark_ring_color) {
      return `${status.bg_color} ${status.text_color} ${status.ring_color} ${status.dark_bg_color} ${status.dark_text_color} ${status.dark_ring_color}`;
    }
    
    // Fallback for old system or missing status data
    let statusId: number;
    
    // Handle case where status is an object
    if (typeof status === 'object' && status.id) {
      statusId = status.id;
    } else if (typeof status === 'number') {
      statusId = status;
    } else {
      return 'bg-slate-50 text-slate-700 ring-slate-600/20 dark:bg-slate-900/30 dark:text-slate-400 dark:ring-slate-500/50';
    }
    
    // Return appropriate classes based on status ID
    switch (statusId) {
      case 1:
        return 'bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-900/30 dark:text-green-400 dark:ring-green-500/50';
      case 2:
        return 'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-900/30 dark:text-red-400 dark:ring-red-500/50';
      case 3:
        return 'bg-yellow-50 text-yellow-700 ring-yellow-600/20 dark:bg-yellow-900/30 dark:text-yellow-400 dark:ring-yellow-500/50';
      case 4:
        return 'bg-gray-50 text-gray-700 ring-gray-600/20 dark:bg-gray-900/30 dark:text-gray-400 dark:ring-gray-500/50';
      default:
        return 'bg-slate-50 text-slate-700 ring-slate-600/20 dark:bg-slate-900/30 dark:text-slate-400 dark:ring-slate-500/50';
    }
  }

}
